/**
 * Conversation pane for the plugin builder.
 * Displays chat messages, streaming text, and guided flow panels.
 */

import { useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface PluginConversationPaneProps {
  messages: Message[];
  isProcessing: boolean;
  streamingText: string;
  emptyStateContent?: React.ReactNode;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  className?: string;
}

function MessageBubble({ message, isStreaming }: { message: Message; isStreaming?: boolean }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-4 py-3',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        <p className="whitespace-pre-wrap text-sm">
          {message.content}
          {isStreaming && <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />}
        </p>
      </div>
    </div>
  );
}

export function PluginConversationPane({
  messages,
  isProcessing,
  streamingText,
  emptyStateContent,
  headerContent,
  footerContent,
  className,
}: PluginConversationPaneProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText, scrollToBottom]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {headerContent}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          emptyStateContent ? (
            emptyStateContent
          ) : (
            <div className="flex flex-col items-center justify-center h-full px-4">
              <div className="max-w-lg space-y-4">
                <h3 className="text-lg font-semibold text-foreground text-center">
                  Plugin Builder
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground text-center">
                  Build custom plugins using React and TypeScript. Describe what you want to create,
                  and I&apos;ll write the code for you.
                </p>
                <p className="text-sm font-medium text-foreground">I can help you:</p>
                <ul className="text-sm space-y-2 text-muted-foreground list-disc pl-5">
                  <li>
                    <strong className="text-foreground">Build React components</strong> with
                    TypeScript and the Fias SDK
                  </li>
                  <li>
                    <strong className="text-foreground">Create interactive UIs</strong> with state
                    management, forms, and data display
                  </li>
                  <li>
                    <strong className="text-foreground">Integrate with AI Entities</strong> using
                    the SDK&apos;s entity invocation hooks
                  </li>
                  <li>
                    <strong className="text-foreground">Preview in real-time</strong> as code is
                    generated
                  </li>
                </ul>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  What kind of plugin would you like to build?
                </p>
              </div>
            </div>
          )
        ) : (
          messages.map((message, index) => <MessageBubble key={index} message={message} />)
        )}

        {isProcessing &&
          (streamingText ? (
            <MessageBubble message={{ role: 'assistant', content: streamingText }} isStreaming />
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Fias AI is thinking...</span>
            </div>
          ))}
      </div>
      {footerContent}
    </div>
  );
}
